import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import {
  Grid,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { Settings2 } from "lucide-react";
import {
  cloneElement,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  StyledTabsFilters,
  StyledTab,
  StyledTabBox,
  StyledTabContent,
  TitleTypography
} from "./TabsFilters.styles";
import type { TabsFiltersProps } from "./TabsFilters.types";
import { ButtonDigited } from "../../components/Button/Button.variants";
import type { ArrayElement } from "../../types";
import { Icon } from "../Icon";
import { Select } from "../Select/Select.component";

/**
 * this component expects filter and setFilter to be set as state of the parent,
 * where an unfiltered case is represented by the empty string
 * @param param0
 * @returns
 */
export function TabsFilters<T extends string>({
  contents,
  count,
  filter,
  options,
  setFilter,
  title,
  showGridTabView=true,//se viene valorizzata questa prop a FALSE viene nascosto lo switch di visualizzazione GRID/LIST (ESEMPIO: pagina PREFERITI)
  ...props
}: TabsFiltersProps<T>) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(`${contents?.[1]?.iconTab}-1`);
  const { t } = useTranslation();

  // NOTE: it would be much easier to just export a type alias from the types file
  const detectIconTab = ({ iconTab }: Pick<ArrayElement<typeof contents>, "iconTab">) => {
    switch (iconTab){
    case "list":
      return "Icons_Listato";
    case "grid":
      return "icons_griglia";
    case "square" :
      return "Icons_Listato_modulo";
    default : return "Icons_Listato_modulo";
    }
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // TO GET Code of Current Product
  // const urlSearchParams = new URLSearchParams(window.location.search);
  // const params = Object.fromEntries(urlSearchParams.entries());
  // const courseCode = params?.id || "";

  return (
    <StyledTabsFilters
      className={ "modules-tab"  }
      { ...props }
    >
      <TabContext value={ showGridTabView ? value : `${contents[0].iconTab}-${0}` }>
        <StyledTabBox>
          <Grid
            display={ "flex" }
            flexDirection={ "column" }
            gap={ 1.75 }
            width={ isMobile ? "100%" : "70%" }>
            <TitleTypography variant="h2">
              { title }
            </TitleTypography>
            {
              count ? (
                <Typography
                  sx={ {
                    [theme.breakpoints.down("sm")]:{
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      lineHeight: "1.28571"
                    }
                  } }>
                  { count + " " + t("favourite_saved") }
                </Typography>
              ) : null
            }
          </Grid>
          <Grid
            display={ "flex" }
            alignItems={ "flex-end" }
          >
            <Grid
              tabIndex={ 0 }
              className={ `${filter || open ? "filters selected" : " filters" }` }
              alignItems={ "center" }
              display={ "flex" }
              flexDirection={ "column" }
              marginRight={ "28px" }
              position={ "relative" }
              sx={ {
                ".MuiFormControl-root ":{
                  position:"absolute",
                  top:"-1.875rem"
                },
                [theme.breakpoints.down("sm")]:{
                  marginRight:"7px"
                }
              } }
              onKeyDown={ (e) => {
                if (e.code === "Enter") {
                  e.preventDefault();
                  setOpen((current) => !current);
                }
              } }
            >
              <ButtonDigited
                typology="secondary"
                aria-label={ t("filter") }
                size="small"
                onClick={ ()=> setOpen(!open)
                }
                sx={ {
                  gap: "0.5rem",
                  padding: "1rem 1.5rem",
                  color:theme.customColors.textPrimary
                } }
              >
                <div style={ {  display: "flex", alignItems: "center", gap: "0.5rem", color:theme.customColors.textPrimary } }>
                  <Settings2 size={ "1rem" } />
                  { t("filters") }
                </div>
              </ButtonDigited>
              <Select
                options={ options }
                id={ t("filter") || undefined }
                open={ open }
                onClose={ () => setOpen(false) }
                onOpen={ () => setOpen(true) }
                onChange={ (changeEvent) => {
                  if (!setFilter) return;
                  if (changeEvent.target.value === filter) {
                    setFilter("" as T);
                  } else {
                    setFilter(changeEvent.target.value as T);
                  }
                } }
                onClick={ (clickEvent) => {
                  if (!setFilter) return;
                  if ((clickEvent.target as HTMLOptionElement).selected) {
                    setFilter("" as T);
                  }
                } }
                value={ filter }
              />
            </Grid>
            { showGridTabView &&
              <Grid
                aria-label={ t("close_widget_drawer") }
                alignItems={ "center" }
                display={ "flex" }
                flexDirection={ "column" }
                sx={ {
                  ".MuiTabs-root":{
                    minHeight:"unset"
                  }
                } }
                onKeyDown={ (e) => {
                  if (e.code === "Enter") {
                    e.preventDefault();
                    setValue((current) => {
                      const valuePositionIndex = contents.findIndex((item, idx) => current === `${item.iconTab}-${idx}`);
                      if(valuePositionIndex > -1 && valuePositionIndex < (contents.length -1)){
                        return `${contents[valuePositionIndex +1].iconTab}-${valuePositionIndex +1}`;
                      }
                      return `${contents[0].iconTab}-${0}`;
                    });
                  }
                } }
              >
                <TabList
                  onChange={ (_, newValue) => setValue(newValue) }
                  aria-label={ value === "grid-0" ? t("tab_view") : t("grid_view") }
                  sx={ {
                    "button":{
                      ontWeight:"400",
                      margin:"0 0 0 7px",
                      minHeight:"unset",
                      minWidth:"unset",
                      opacity:1,
                      padding:"8px 0",
                      [theme.breakpoints.down("sm")]:{
                        margin:"0 0 0 8px"
                      },

                      ".icon": {
                        height:"17px",
                        width:"17px",
                        "path":{
                          fill: theme.customColors.textPrimary
                        }
                      },

                      "&.Mui-selected ": {
                        ".icon": {
                          "path": {
                            fill: theme.customColors.systemSecondary01
                          },

                          "&.square": {
                            background: alpha(theme.customColors.systemSecondary01, 0.5)
                          }
                        }
                      }
                    }
                  } }
                >
                  {
                    contents.map((item, idx)=> {
                      const icon = detectIconTab({ iconTab: item.iconTab });
                      return (
                        <StyledTab
                          tabIndex={ -1 }
                          key={ `${item.iconTab}-${idx}` }
                          aria-label={
                            item.iconTab === "grid"
                              ? t("grid_view")
                              : t("tab_view")
                          }
                          icon={
                            <Icon
                              role={ "img" }
                              aria-label={ value === "grid-0" ? t("tab_view") : t("grid_view") }
                              aria-hidden="true"
                              color={ theme.customColors.systemPrimary01 }
                              className={ `icon ${item.iconTab}` }
                              icon={ icon }

                            />
                          }
                          value={ `${item.iconTab}-${idx}` }
                        />
                      );
                    })
                  }
                </TabList>
                <Typography
                  aria-hidden="true"
                  sx={ {
                    [theme.breakpoints.down("sm")]:{
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      lineHeight: "0.875rem"
                    },
                    color: theme.customColors.textPrimary
                  } }
                >{ t("watch") }</Typography>
              </Grid>
            }
          </Grid>
        </StyledTabBox>
        {
          contents.map(({ components , iconTab }, idx)=> (
            <StyledTabContent value={ `${iconTab}-${idx}` } key={ `${iconTab}-${idx}` }>
              {
                components.map((Element, index: number) => {
                  return cloneElement(Element, { key:`itemElement-${index}` });
                })

              }
            </StyledTabContent>
          ))
        }
      </TabContext>
    </StyledTabsFilters>
  );
}
